import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate, Link } from 'react-router-dom';

export default function RegisterPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!acceptTerms) {
            setError('Musisz zaakceptować regulamin i politykę prywatności, by kontynuować.');
            return;
        }

        // Check ref from URL param (Affiliate logic)
        const urlParams = new URLSearchParams(window.location.search);
        const refId = urlParams.get('ref') || null;

        const { data: authData, error: authError } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    first_name: firstName,
                    last_name: lastName,
                    referred_by_id: refId,
                    terms_accepted_at: new Date().toISOString()
                }
            }
        });

        if (authError) {
            setError(authError.message);
        } else {
            setSuccess('Zarejestrowano pomyślnie. Zaloguj się!');
            setTimeout(() => navigate('/zaloguj'), 2000);
        }
    };

    return (
        <div className="min-h-screen bg-bg-dark flex items-center justify-center p-4">
            <div className="bg-bg-card p-8 rounded-2xl border border-white/10 w-full max-w-md">
                <h2 className="text-2xl font-bold text-white mb-6 text-center">Dołącz do Akademii</h2>

                {error && (
                    <div id="register-error" role="alert" className="bg-red-500/10 border border-red-500/20 text-red-500 p-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {success && (
                    <div role="status" className="bg-green-500/10 border border-green-500/20 text-green-500 p-3 rounded-lg mb-6">
                        {success}
                    </div>
                )}

                <form onSubmit={handleRegister} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-400 mb-1">Imię</label>
                            <input
                                id="firstName"
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                aria-describedby={error ? "register-error" : undefined}
                                className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary/50 focus-visible:ring-2 focus-visible:ring-primary/50"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-400 mb-1">Nazwisko</label>
                            <input
                                id="lastName"
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary/50 focus-visible:ring-2 focus-visible:ring-primary/50"
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="reg-email" className="block text-sm font-medium text-gray-400 mb-1">Email</label>
                        <input
                            id="reg-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            aria-invalid={!!error}
                            aria-describedby={error ? "register-error" : undefined}
                            className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary/50 focus-visible:ring-2 focus-visible:ring-primary/50"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="reg-password" className="block text-sm font-medium text-gray-400 mb-1">Hasło</label>
                        <input
                            id="reg-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            aria-invalid={!!error}
                            aria-describedby={error ? "register-error" : undefined}
                            className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary/50 focus-visible:ring-2 focus-visible:ring-primary/50"
                            required
                        />
                    </div>

                    <div className="pt-2">
                        <label htmlFor="acceptTerms" className="flex items-start gap-3 cursor-pointer group">
                            <div className="relative flex items-center justify-center w-5 h-5 mt-0.5 shadow-sm">
                                <input
                                    id="acceptTerms"
                                    type="checkbox"
                                    required
                                    checked={acceptTerms}
                                    onChange={(e) => setAcceptTerms(e.target.checked)}
                                    className="peer sr-only"
                                />
                                <div className="w-5 h-5 border-2 border-white/20 rounded bg-black/30 peer-checked:bg-primary peer-checked:border-primary transition-all duration-200"></div>
                                <svg className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-all duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg>
                            </div>
                            <span className="text-sm text-gray-400 group-hover:text-white transition-colors">
                                Akceptuję <Link to="/aktualnosci/regulamin-serwisu" target="_blank" className="text-primary hover:text-white transition-colors">Regulamin Serwisu</Link> oraz <Link to="/aktualnosci/polityka-prywatnosci" target="_blank" className="text-primary hover:text-white transition-colors">Politykę Prywatności</Link>. *
                            </span>
                        </label>
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-primary hover:bg-primary-light text-white font-medium py-2.5 rounded-lg transition-colors mt-2"
                    >
                        Zarejestruj się
                    </button>
                </form>

                <p className="text-center mt-6 text-gray-400 text-sm">
                    Masz już konto? <Link to="/zaloguj" className="text-primary hover:text-primary-light transition-colors">Zaloguj się</Link>
                </p>
            </div>
        </div>
    );
}
